import React, { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'
import { CSSTransition } from 'react-css-transition'
import { Auth } from 'aws-amplify'
import Textbox from '../Textbox'
import Button from '../Button'
import Spinner from '../Spinner'
import TransitionStyles from '../../lib/transitionStyles'

class ForgotPassword extends Component {

  constructor(props) {
    super(props)
    this.state = {
      email: '',
      password: '',
      confirmPassword: '',
      showResetWithCode: false,
      isLoading: false,
      errorMsg: {}
    }
    this.handleChange = this.handleChange.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.renderResetWithCodeForm = this.renderResetWithCodeForm.bind(this)
    this.renderResetConfirmed = this.renderResetConfirmed.bind(this)
    this.resetPassword = this.resetPassword.bind(this)
    this.afterForgotPasswordAction = props.afterForgotPasswordAction.bind(this)
  }

  handleChange(event) {
    this.setState({
      [event.target.id]: event.target.value
    })
  }

  async handleSubmit(e) {
    e.preventDefault()
    const { email } = this.state // , password, confirmPassword

    try {
      this.setState({isLoading: true})
      await Auth.forgotPassword(email)
      this.setState({isLoading: false, showResetWithCode: true, showError: false, errorMsg: null})
    } catch (error) {
      this.setState({
        isLoading: false, 
        showError: true, 
        errorMsg: <>
          <div className="font-bold">
            <FormattedMessage id="login.forgotpass.passwordResetProblem" defaultMessage="Problem resetting password" />
          </div>
          <div className="text-sm">
            {error.message}
          </div>
        </>})
      console.error(error)
    }
  }

  async resetPassword() {
    const { email, resetCode, password, confirmPassword } = this.state

    if (!resetCode || !password || !confirmPassword) {
      this.setState({ showError: true, errorMsg: <FormattedMessage id="login.forgotpass.oneFieldIsEmpty" defaultMessage="One field is empty" /> })
    } else if (password !== confirmPassword) {
      this.setState({ showError: true, errorMsg: <FormattedMessage id="login.forgotpass.twoPasswordsNotIdentical" defaultMessage="The two passwords are not identical" /> })
    } else {

      try {
        this.setState({isLoading: true})
        await Auth.forgotPasswordSubmit(
          email,
          resetCode,
          password
        );
        this.setState({ resetConfirmed: true, showResetWithCode: false, isLoading: false, showError: false, errorMsg: null });
      } catch (error) {
        this.setState({
          showError: true,
          errorMsg: <>
            <div className="font-bold">
              <FormattedMessage id="login.forgotpass.failedToResetPassword" defaultMessage="Failed to reset password" />
            </div>
            <div className="text-sm">
              { error.message }
            </div>
          </>,
          isLoading: false
        });
      }
    }
  }

  renderResetConfirmed() {
    const { props } = this
    const { email, password } = this.state

    return(
      <div>
        <h3>
        <FormattedMessage id="login.forgotpass.newPasswordSaved" defaultMessage="New password saved" />
        </h3>
        <p>
        <FormattedMessage id="login.forgotpass.youCanLoginWithPassword" defaultMessage="You can now log in with your new password" />
        </p>
        <div className="flex justify-end">
          <Button
            onClick={() => props.afterForgotPasswordAction(email, password)}
          >
            <>
            <FormattedMessage id="login.forgotpass.goToLogin" defaultMessage="Go to login" />
            </>
          </Button>
        </div>
      </div>
    )
  }

  renderResetWithCodeForm() {
    const { resetCode, password, confirmPassword, showError, errorMsg } = this.state
    return(
      <CSSTransition {...TransitionStyles} transitionAppear active>

        <h2><FormattedMessage id="login.forgotpass.createNewCode" defaultMessage="Create new code" /></h2>

        <div className="mt-4 border-b pb-4 w-full md:w-1/2">
          <label
            htmlFor="resetCode"
            className="text-sky text-sm font-bold"
          >
            <FormattedMessage id="login.forgotpass.resetCodeSentByMail" defaultMessage="A reset code has been sent by email" />
            <div>
              <Textbox
                htmlId="resetCode"
                htmlName="resetCode"
                value={resetCode}
                onChange={this.handleChange}
                type="text"
                className="w-full"
              />
            </div>
          </label>
        </div>

        <div className="mt-4">
          <label
            htmlFor="password"
            className="text-sky text-sm font-bold"
          >
            <FormattedMessage id="login.forgotpass.newPassword" defaultMessage="New password" />
            <div>
              <Textbox
                htmlId="password"
                htmlName="password"
                value={password}
                onChange={this.handleChange}
                type="password"
                className="w-full md:w-1/2"
              />
            </div>
          </label>
        </div>

        <div className="mt-4">
          <label
            htmlFor="confirmPassword"
            className="text-sky text-sm font-bold"
          >
            <FormattedMessage id="login.forgotpass.repeatNewPassword" defaultMessage="Repeat new password" />
            <div>
              <Textbox
                htmlId="confirmPassword"
                htmlName="confirmPassword"
                value={confirmPassword}
                onChange={this.handleChange}
                type="password"
                className="w-full md:w-1/2"
              />
            </div>
          </label>
        </div>

        {showError && (
          <div className="bg-red-lightest text-red p-4 border-l-2 border-red my-4">
            {errorMsg}
          </div>
        )}
        
        <div className="flex justify-end">
          <Button
            onClick={this.resetPassword}
          >
            <>
              <FormattedMessage id="login.forgotpass.saveNewPassword" defaultMessage="Save new password" />
            </>
          </Button>
        </div>


      </CSSTransition>
    )
  }

  renderForm() {
    const { email, showError, errorMsg } = this.state

    return (
      <form onSubmit={this.handleSubmit}>

        <div className="bg-grass text-sm p-4">
          <span className="font-bold"><FormattedMessage id="login.forgotpass.pleaseNoteLabel" defaultMessage="NB" />:</span>
          <FormattedMessage id="login.forgotpass.newResetCodeInstructions" defaultMessage="We will send you a 6 digit reset code per email
You need to enter this code on the next screen in order to create a new password" />
        </div>

        <div className="mt-4">
          <label
            htmlFor="email"
            className="text-sky text-sm font-bold"
          >
            E-mail
            <div>
              <Textbox
                htmlId="email"
                htmlName="email"
                type="text"
                value={email}
                onChange={this.handleChange}
                className="w-full md:w-1/2"
              />
            </div>
          </label>
        </div>

        {showError && (
          <div className="bg-red-lightest text-red p-4 border-l-2 border-red my-4">
            {errorMsg}
          </div>
        )}        

        <div className="flex justify-end mt-8">
          <Button>
            <FormattedMessage
              id="login.forgotpass.sendResetCodeLabel"
              defaultMessage="Send reset code"
            />
          </Button>
        </div>
      </form>      
    )
  }

  render() {
    const { isLoading, showResetWithCode, resetConfirmed } = this.state

    if (isLoading) {
      return <Spinner />
    }

    if (showResetWithCode) {
      return this.renderResetWithCodeForm()
    }
    
    return (
      <>
        <h1>
          <FormattedMessage
            id="booking.amountForm.login.forgotPassword"
            defaultMessage="Forgot password"
          />
        </h1>

        {resetConfirmed ? (
          this.renderResetConfirmed()
        ) : (
          this.renderForm()
        )}
      </>
    )
  }
}

ForgotPassword.propTypes = {
  afterSignupAction: PropTypes.func.isRequired
}

export default ForgotPassword